import { Dispatch } from 'react';
import { isObjectLike } from 'lodash';

import { refetchSnapEligibleProductsAction, updateSnapStatusSuccessAction } from 'containers/Snaps/Snap/store';

import {
  DeleteMatchResponse,
  ProductLineItemMatch,
  ProductLineItemMatchUpdate,
  ProductMatchesWithSnapState,
  ProductMatchWithSnapState,
  SnapProductMatchesResponse,
} from 'codegen';
import {
  IDataResponse,
  InitialTableActions,
  TRequestCallback,
} from 'types';

import { getFormattedMatchedProductItem } from './mappers';

export const handleSuccessGetMatchesLineItems = (
  setMatchesData: Dispatch<ProductLineItemMatch[] | undefined>,
  setMatchesStatuses: Dispatch<Omit<SnapProductMatchesResponse, 'matchedItems'>>,
) => (
  { data }: IDataResponse<SnapProductMatchesResponse>,
) => {
  const { matchedItems, ...rest } = data;

  setMatchesData(matchedItems?.map(getFormattedMatchedProductItem));
  setMatchesStatuses(rest);
};

export const handlePatchMatchesLineItem = (
  onPatchTableData: TRequestCallback<ProductLineItemMatchUpdate>,
  snapId?: string,
) => (
  { id, lineItem, matchedProduct, matchedByIdentities, quantity }: ProductLineItemMatch,
  actionType?: InitialTableActions,
) => {
  const isApprove = actionType === InitialTableActions.Approve;

  const payload = {
    quantity,
    lineItemId: isObjectLike(lineItem) ? lineItem.id : Number(lineItem),
    matchedProductId: isObjectLike(matchedProduct) ? matchedProduct?.id : String(matchedProduct),
    matchedProductIdentityId: Array.isArray(matchedByIdentities) ? matchedByIdentities?.[0].value : String(matchedByIdentities),
  };

  onPatchTableData({
    payload,
    callbackURL: `/snaps/${snapId}/matches/${id}`,
    callbackQueryParams: { isApprove },
  });
};

export const handleSuccessPatchMatchesLineItem = (
  refresh: () => void,
) => (
  { data }: IDataResponse<ProductMatchWithSnapState>,
) => {
  const { snap } = data;

  refresh();
  updateSnapStatusSuccessAction(snap?.state);
};

export const handlePostMatchesLineItem = (
  onPostTableData: TRequestCallback<ProductLineItemMatchUpdate>,
) => (
  { lineItem, matchedProduct, matchedByIdentities, quantity }: ProductLineItemMatch,
) => {
  // will be primitive values due to form, but expect object
  const payload = {
    quantity,
    lineItemId: Number(lineItem),
    matchedProductId: String(matchedProduct),
    matchedProductIdentityId: String(matchedByIdentities),
  };

  onPostTableData({ payload });
};

export const handleSuccessPostMatchesLineItem = (
  refresh: () => void,
) => (
  { data }: IDataResponse<ProductMatchWithSnapState>,
) => {
  const { snap } = data;

  refresh();
  updateSnapStatusSuccessAction(snap?.state);
};

export const handleDeleteMatchesLineItem = (
  onDeleteTableData: TRequestCallback,
  snapId?: string,
) => (
  id: number,
) => {
  onDeleteTableData({ callbackURL: `/snaps/${snapId}/matches/${id}` });
};

export const handleSuccessDeleteMatchesLineItem = (
  refresh: () => void,
) => (
  { data }: IDataResponse<DeleteMatchResponse>,
) => {
  refresh();
  updateSnapStatusSuccessAction(data.state);
};

export const handleRematchProducts = (
  canHaveOfferMatches: boolean,
  setMatchesData: Dispatch<ProductLineItemMatch[]>,
) => (
  { data }: IDataResponse<ProductMatchesWithSnapState>,
) => {
  const { productMatch } = data;

  setMatchesData(productMatch.map(getFormattedMatchedProductItem));
  updateSnapStatusSuccessAction(data.snap?.state);

  if (canHaveOfferMatches) {
    refetchSnapEligibleProductsAction();
  }
};
